import {
  Avatar,
  Card,
  Flex,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { Pen } from "lucide-react";
import styles from "./styles.module.scss";

import { PlusOutlined } from "@ant-design/icons";
import { ToastService } from "@shared/lib/services";
import {
  ACCOUNT_STATUS,
  IBrand,
  IGetOrganisationPermissions,
  IOrganisationBrandUser,
} from "@shared/lib/types";
import { IBrandOrganisation } from "@shared/lib/types/brandOrganisation";
import { Button } from "@shared/ui/Button";
import { useEffect, useState } from "react";
import { useEditOrganisationUser } from "../lib/hooks";
import { useSearchBrandUser } from "../lib/hooks/useSearchBrandUser";
import OrganisationUserForm from "./OrganisationUserForm";

export const EmployeesTab = ({
  organisation,
  organisationId,
}: {
  organisation: IBrandOrganisation | undefined;
  organisationId: string;
}) => {
  const { employeesData, isFetchingEmployees, employeesError, isSuccess } =
    useSearchBrandUser({
      brandOrganisationId: organisationId,
      pageNo: 1,
      limit: 3000,
    });
  const [modal, setModal] = useState<{
    organisation: IBrandOrganisation | null;
    open: boolean;
    userPermissions: IGetOrganisationPermissions | null;
    isEdit: boolean;
    brand: IBrand | null;
  }>({
    organisation: null,
    brand: null,
    open: false,
    userPermissions: null,
    isEdit: false,
  });

  const {
    updateOrganisationUser,
    isUpdatingOrganisationUser,
    isUpdatedOrganisationUser,
    updateOrganisationUserError,
    editOrganisationBrandUserId,
  } = useEditOrganisationUser(organisationId);

  useEffect(() => {
    if (isUpdatedOrganisationUser) {
      ToastService.success("Employee updated successfully");
    } else if (updateOrganisationUserError) {
      ToastService.error(updateOrganisationUserError.message);
    }
  }, [isUpdatedOrganisationUser, updateOrganisationUserError]);

  const getEditModalTitle = () => {
    const prefix = modal.isEdit ? "Edit" : "Add";
    let postfix = "";
    if (!modal.userPermissions) {
      postfix = "Brand";
    } else if (modal.userPermissions?.userType === "brand-user") {
      postfix = "Employee";
    } else {
      postfix = "Member";
    }
    return `${prefix} ${postfix}`;
  };

  return (
    <div className={styles.tabPane}>
      <Card
        title="Company Users"
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() =>
              setModal({
                open: true,
                userPermissions: {
                  userType: "brand-user",
                } as IGetOrganisationPermissions,
                organisation: organisation || null,
                isEdit: false,
                brand: null,
              })
            }
          >
            Add Employee
          </Button>
        }
        className={styles.card}
        bodyStyle={{ padding: "1px 0 0 0" }}
      >
        <Table
          dataSource={employeesData?.data?.data || []}
          rowKey={(record) => record._id}
          pagination={false}
          className={styles.table}
          columns={[
            {
              title: "Employee",
              key: "employee",
              render: (_, record: IOrganisationBrandUser) => (
                <Flex align="center" gap={12}>
                  <Avatar shape="square" size={40} className={styles.empAvatar}>
                    {record?.firstName?.[0]}
                  </Avatar>
                  <Flex vertical>
                    <Typography.Text strong>
                      {record.firstName} {record.lastName}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      className={styles.secondaryText}
                    >
                      {record?.emailId}
                    </Typography.Text>
                  </Flex>
                </Flex>
              ),
            },
            {
              title: "Role",
              key: "role",
              render: (_, record: IOrganisationBrandUser) => (
                <Tag>{record?.role}</Tag>
              ),
            },
            {
              title: "Status",
              key: "status",
              render: (_, record: IOrganisationBrandUser) => (
                <Switch
                  checked={record?.accountStatus === ACCOUNT_STATUS.ACTIVE}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  onChange={() => {
                    updateOrganisationUser({
                      brandUserId: record?._id || "",
                      brandUser: {
                        accountStatus:
                          record?.accountStatus === ACCOUNT_STATUS.ACTIVE
                            ? ACCOUNT_STATUS.INACTIVE
                            : ACCOUNT_STATUS.ACTIVE,
                      },
                    });
                  }}
                  loading={
                    isUpdatingOrganisationUser &&
                    editOrganisationBrandUserId === record?._id
                  }
                />
              ),
            },
            {
              title: "Is Test User?",
              key: "isTestUser",
              render: (_, record: IOrganisationBrandUser) => (
                <Tag>{record?.isTestUser ? "Yes" : "No"}</Tag>
              ),
            },
            {
              title: "Actions",
              key: "actions",
              align: "right",
              render: (_, record: IOrganisationBrandUser) => (
                <Space>
                  <Button
                    type="text"
                    onClick={() => {
                      setModal({
                        open: true,
                        userPermissions: {
                          brandIds:
                            record?.brands?.map((brand) => brand._id) || [],
                          createdOn: record?.createdAt || "",
                          permissions:
                            record?.permissionGroups?.map(
                              (group) => group._id
                            ) || [],
                          updatedAt: record?.updatedAt || "",
                          userId: record?._id || "",
                          userType: "brand-user",
                          user: {
                            ...record,
                            isTestUser: record?.isTestUser || false,
                          },
                          userModelName: "brand-user",
                        },
                        organisation: organisation || null,
                        isEdit: true,
                        brand: null,
                      });
                    }}
                    icon={<Pen size={16} />}
                  />
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        open={modal.open}
        title={getEditModalTitle()}
        destroyOnClose
        onCancel={() =>
          setModal({
            open: false,
            userPermissions: null,
            organisation: null,
            isEdit: false,
            brand: null,
          })
        }
        footer={null}
      >
        <OrganisationUserForm
          organisationId={modal.organisation?._id || ""}
          userPermissions={modal.userPermissions}
          isEdit={modal.isEdit}
          onCancel={() =>
            setModal({
              open: false,
              userPermissions: null,
              organisation: null,
              isEdit: false,
              brand: null,
            })
          }
        />
      </Modal>
    </div>
  );
};
